import { List, Button, Tag, Modal, Spin, Tooltip } from "antd";
import { useState } from "react";
import { EditFilled, DeleteFilled, LockFilled, UnlockFilled, StarFilled, StarOutlined } from "@ant-design/icons";
import Toast from "../../helpers/Toast";
import { catchError } from "../../services/DaoService";
import { PrioriteService } from "../../services/PrioriteService";
import PrioriteEditForm from "./PrioriteEditForm";
import PropTypes from "prop-types";
import styles from "./PrioriteList.module.scss";

const PrioriteList = ({ paginationData, setPaginationData, priorites, setPriorites, searchParams }) => {
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [updateModalVisible, setUpdateModalVisible] = useState(false);
  const [selectedPriorite, setSelectedPriorite] = useState(null);

  const loadMore = () => {
    if (paginationData?.next_page_url && !loadingMore) {
      setLoadingMore(true);
      PrioriteService.post(paginationData.next_page_url, searchParams)
        .then((response) => {
          if (!response) return;
          setPaginationData(response.data);
          setPriorites((prevPriorites) => [...prevPriorites, ...response.data.data]);
        })
        .catch(catchError)
        .finally(() => setLoadingMore(false));
    }
  };

  const handleDelete = (idOrUid) => {
    setLoading(true);
    PrioriteService.getInstance()
      .remove(idOrUid)
      .then((response) => {
        Toast.success(response.message);
        setPriorites((items) => items.filter((item) => item.uid !== idOrUid));
      })
      .catch(catchError)
      .finally(() => setLoading(false));
  };

  const openEditModal = (item) => {
    setSelectedPriorite(item);
    setUpdateModalVisible(true);
  };

  const handleEnable = (priorite) => {
    PrioriteService.getInstance()
      .enable(priorite.uid)
      .then((response) => {
        Toast.success(response.message);
        setPriorites((prevPriorites) =>
          prevPriorites.map((u) => (u.uid === response.data.uid ? response.data : u))
        );
      })
      .catch((error) => Toast.error(error.message));
  };

  const handleDisable = (priorite) => {
    PrioriteService.getInstance()
      .disable(priorite.uid)
      .then((response) => {
        Toast.success(response.message);
        setPriorites((prevPriorites) =>
          prevPriorites.map((u) => (u.uid === response.data.uid ? response.data : u))
        );
      })
      .catch((error) => Toast.error(error.message));
  };

  const handleUpdate = (item) => {
    // if updated item is now default, clear is_default on all others
    setPriorites((prevPriorites) =>
      prevPriorites.map((u) => {
        if (u.uid === item.uid) return item;
        if (item.is_default) return { ...u, is_default: false };
        return u;
      })
    );
    setSelectedPriorite(null);
    setUpdateModalVisible(false);
  };

  const handleSetDefault = (priorite) => {
    PrioriteService.getInstance()
      .setDefault(priorite.uid)
      .then((response) => {
        Toast.success(response.message);
        setPriorites((prev) =>
          prev.map((u) => ({ ...u, is_default: u.uid === response.data.uid }))
        );
      })
      .catch((error) => Toast.error(error.message));
  };

  return (
    <>
      <Spin spinning={loading}>
        <List
          className={styles.prioriteList}
          itemLayout="horizontal"
          dataSource={priorites}
          renderItem={(item) => (
            <List.Item
              className={styles.listItem}
              actions={[
                <Tooltip title={item.is_default ? "Priorité par défaut" : "Définir par défaut"} key="default">
                  <Button
                    icon={item.is_default ? <StarFilled style={{ color: '#FAAD14' }} /> : <StarOutlined />}
                    onClick={() => !item.is_default && handleSetDefault(item)}
                    type="text"
                    disabled={item.is_default}
                  />
                </Tooltip>,
                <Tooltip title="Modifier" key="edit">
                  <Button
                    icon={<EditFilled />}
                    onClick={() => openEditModal(item)}
                    type="text"
                  />
                </Tooltip>,
                <Tooltip title={item.enabled ? "Désactiver" : "Activer"} key="toggle">
                  <Button
                    icon={item.enabled ? <LockFilled /> : <UnlockFilled />}
                    onClick={() => {
                      Modal.confirm({
                        title: `Voulez-vous ${item.enabled ? "désactiver" : "activer"} cette priorité ?`,
                        okText: item.enabled ? "Désactiver" : "Activer",
                        cancelText: "Annuler",
                        okType: item.enabled ? "danger" : "primary",
                        onOk: () => (item.enabled ? handleDisable(item) : handleEnable(item)),
                      });
                    }}
                    type="text"
                  />
                </Tooltip>,
                <Tooltip title="Supprimer" key="delete">
                  <Button
                    icon={<DeleteFilled />}
                    onClick={() =>
                      Modal.confirm({
                        title: "Voulez-vous vraiment supprimer cette priorité ?",
                        okText: "Confirmer",
                        cancelText: "Annuler",
                        okType: "danger",
                        onOk: () => handleDelete(item.uid),
                      })
                    }
                    type="text"
                    danger
                  />
                </Tooltip>,
              ]}
            >
              <List.Item.Meta
                title={<span className={styles.prioriteTitle}>{item.nom}</span>}
                description={
                  <div className={styles.metaDescription}>
                    <Tag color={item.enabled ? "success" : "error"}>
                      {item.enabled ? "Activée" : "Désactivée"}
                    </Tag>
                    {item.is_default && <Tag color="gold" icon={<StarFilled />}>Par défaut</Tag>}
                    <Tag className={styles.colorTag} style={{ backgroundColor: item.couleur }}>
                      {item.couleur}
                    </Tag>
                  </div>
                }
              />
            </List.Item>
          )}
        />
        {loadingMore && (
          <div className={styles.loadMore}>
            <Button onClick={loadMore} loading={loadingMore}>
              Charger plus
            </Button>
          </div>
        )}
      </Spin>

      {selectedPriorite && (
        <PrioriteEditForm
          visible={updateModalVisible}
          uid={selectedPriorite.uid}
          onUpdate={handleUpdate}
          onCancel={() => setUpdateModalVisible(false)}
        />
      )}
    </>
  );
};

export default PrioriteList;

PrioriteList.propTypes = {
  paginationData: PropTypes.object.isRequired,
  searchParams: PropTypes.object,
  setPaginationData: PropTypes.func.isRequired,
  priorites: PropTypes.array.isRequired,
  setPriorites: PropTypes.func.isRequired,
};
